/**
 * Association Service - Frontend
 * @description Service for association-related API operations
 */

import { api } from '../config';
import {
  ApiResponse,
  AssociationResponse,
  AssociationWithDetailsResponse,
  DeviceAccessType,
} from '@/types';

export interface AssociationCreateRequest {
  userId: string;
  deviceId: string;
  typeOfAccess: DeviceAccessType;
}

export interface AssociationUpdateRequest {
  deviceAccess: Array<{
    deviceId: string;
    typeOfAccess: DeviceAccessType;
  }>;
}

export interface DeviceAccessUpdateRequest {
  userAccess: Array<{
    userId: string;
    typeOfAccess: DeviceAccessType;
  }>;
}

const createSingleAssociation = async (
  userId: string,
  deviceId: string,
  typeOfAccess: DeviceAccessType,
): Promise<ApiResponse<AssociationResponse>> => {
  const response = await api.post<ApiResponse<AssociationResponse>>(
    `/associations/user/${userId}/device/${deviceId}`,
    { typeOfAccess }
  );
  return response.data;
};

const deleteSingleAssociation = async (
  userId: string,
  deviceId: string
): Promise<ApiResponse<void>> => {
  const response = await api.delete<ApiResponse<void>>(
    `/associations/user/${userId}/device/${deviceId}`
  );
  return response.data;
};

export const associationService = {
  // Get all associations for a user (only IDs)
  async getUserAssociations(userId: string): Promise<ApiResponse<AssociationResponse[]>> {
    const response = await api.get<ApiResponse<AssociationResponse[]>>(
      `/associations/user/${userId}`
    );
    return response.data;
  },

  // Get all associations for a user with populated device objects
  async getUserAssociationsWithDevices(
    userId: string
  ): Promise<ApiResponse<AssociationWithDetailsResponse[]>> {
    const response = await api.get<ApiResponse<AssociationWithDetailsResponse[]>>(
      `/associations/user/${userId}?populate=true`
    );
    return response.data;
  },

  // Get all associations for a device (only IDs)
  async getDeviceAssociations(deviceId: string): Promise<ApiResponse<AssociationResponse[]>> {
    const response = await api.get<ApiResponse<AssociationResponse[]>>(
      `/associations/device/${deviceId}`
    );
    return response.data;
  },

  // Get all associations for a device with populated user objects
  async getDeviceAssociationsWithUsers(
    deviceId: string
  ): Promise<ApiResponse<AssociationWithDetailsResponse[]>> {
    const response = await api.get<ApiResponse<AssociationWithDetailsResponse[]>>(
      `/associations/device/${deviceId}?populate=true`
    );
    return response.data;
  },

  // Update user's device associations
  async updateUserAssociations(
    userId: string,
    devices: Array<{ deviceId: string; typeOfAccess: DeviceAccessType }>
  ): Promise<ApiResponse<AssociationResponse[]>> {
    const response = await api.put<ApiResponse<AssociationResponse[]>>(
      `/associations/user/${userId}`,
      {
        deviceAccess: devices,
      }
    );
    return response.data;
  },

  // Update device's user associations
  async updateDeviceAssociations(
    deviceId: string,
    users: Array<{ userId: string; typeOfAccess: DeviceAccessType }>
  ): Promise<ApiResponse<AssociationResponse[]>> {
    const response = await api.put<ApiResponse<AssociationResponse[]>>(
      `/associations/device/${deviceId}`,
      {
        userAccess: users,
      }
    );
    return response.data;
  },

  // Add a single association
  async addAssociation(
    association: AssociationCreateRequest
  ): Promise<ApiResponse<AssociationResponse>> {
    const response = await api.post<ApiResponse<AssociationResponse>>('/associations', association);
    return response.data;
  },

  // Remove a single association
  async removeAssociation(userId: string, deviceId: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(
      `/associations/user/${userId}/device/${deviceId}`
    );
    return response.data;
  },
  createSingleAssociation,
  deleteSingleAssociation,
};
